import { faEyeDropper } from "@fortawesome/pro-solid-svg-icons";
import {
  ButtonBase,
  Popover,
  PopoverProps,
  Stack,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { FC, useEffect, useState } from "react";
import { ColorResult, SketchPicker } from "react-color";
import tinycolor from "tinycolor2";

import { createRgbString } from "../../../utils/styling";
import { Icon } from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import { useColorPickerStore } from "./state";

const PRESET_COLORS: string[] = [
  "#fecaca",
  "#fed7aa",
  "#fef08a",
  "#bbf7d0",
  "#bfdbfe",
  "#e9d5ff",
  "#fbcfe8",
  "#ffffff",
  "#dc2626",
  "#ea580c",
  "#eab308",
  "#16a34a",
  "#2563eb",
  "#9333ea",
  "#db2777",
  "#737373",
  "#991b1b",
  "#9a3412",
  "#854d0e",
  "#166534",
  "#1e40af",
  "#6b21a8",
  "#9d174d",
  "#000000",
];

const StyledColorPicker = styled(SketchPicker)(({ theme }) => ({
  boxShadow: "none !important",
  fontFamily: `${theme.typography.fontFamily} !important`,
  backgroundColor: `${theme.palette.secondary[7]} !important`,
  padding: "0 !important",
  paddingTop: theme.spacing(1),

  "& input": {
    borderRadius: 4,
    color: theme.palette.secondary[1],
    boxShadow: "none !important",
    border: `1px solid ${theme.palette.secondary[6]} !important`,
    backgroundColor: theme.palette.secondary[7],

    "&:focus,&:hover": {
      outline: "none",
      border: `1px solid ${theme.palette.primary.main} !important`,
    },
  },

  "& .flexbox-fix:nth-of-type(3) label": {
    color: `${theme.palette.secondary[1]} !important`,
  },

  "& .flexbox-fix:nth-of-type(4)": {
    borderTop: "none !important",
    padding: `${theme.spacing(0, 0, 0, 2)} !important`,
  },
}));

const COLOR_SIZE = 20;

const Colors = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fill, minmax(${COLOR_SIZE}px, 1fr))`,
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const Color = styled(ButtonBase)<{ color: string }>(({ color }) => ({
  backgroundColor: color,
  borderRadius: 4,
  height: COLOR_SIZE,
  width: COLOR_SIZE,
}));

export interface ColorPickerProps {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  anchorOrigin?: PopoverProps["anchorOrigin"];
  transformOrigin?: PopoverProps["transformOrigin"];
  disableAlpha?: boolean;
  initColor?: string;
  onColorChange?: (color: ColorResult) => void;
  onColorChangeCommitted?: (color: ColorResult) => void;
}

const getColorResult = (color: string): ColorResult => {
  const initialColor = tinycolor(color);
  return {
    hex: initialColor.toHex(),
    rgb: initialColor.toRgb(),
    hsl: initialColor.toHsl(),
  };
};

export const ColorPicker: FC<ColorPickerProps> = (props) => {
  const {
    open,
    onClose,
    anchorEl,
    initColor = "#ffffff",
    anchorOrigin = {
      vertical: "bottom",
      horizontal: "center",
    },
    transformOrigin = {
      vertical: "bottom",
      horizontal: "center",
    },
    disableAlpha,
    onColorChange = () => {},
    onColorChangeCommitted = () => {},
  } = props;

  const [color, setColor] = useState<ColorResult>(getColorResult(initColor));

  const { recentColors, addRecentColor } = useColorPickerStore();

  const isEyeDroppedSupported = !!window.EyeDropper;
  const hasRecentColors = recentColors.length > 0;

  useEffect(() => {
    onColorChange(color);
  }, [color]);

  const handleChangeColor = (newColor: ColorResult) => {
    setColor(newColor);
  };

  const handleCancel = () => {
    setColor(getColorResult(initColor));
    onClose();
  };

  const handleSelect = () => {
    onColorChangeCommitted(color);
    onClose();
  };

  const handleAddRecent = () => {
    addRecentColor(createRgbString(color.rgb));
  };

  const handleEyeDropper = async () => {
    if (isEyeDroppedSupported) {
      try {
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();
        setColor(getColorResult(result.sRGBHex));
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleColorClick = (selectedColor: string) => () => {
    setColor(getColorResult(selectedColor));
  };

  return (
    <Popover
      open={open}
      onClose={handleSelect}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "#191a22",
            p: 2,
          },
        },
        transition: {
          onExited: handleAddRecent,
        },
      }}
    >
      {isEyeDroppedSupported && (
        <Stack
          sx={{
            alignItems: "flex-end",
            mb: 1,
          }}
        >
          <IconButton
            aria-label="Eyedropper"
            variant="quaternary"
            onClick={handleEyeDropper}
          >
            <Icon icon={faEyeDropper} />
          </IconButton>
        </Stack>
      )}
      <StyledColorPicker
        color={color?.rgb || initColor}
        onChange={handleChangeColor}
        disableAlpha={disableAlpha}
        presetColors={[]}
      />
      <Stack sx={{ mt: 1 }}>
        <Typography variant="small" gutterBottom>
          Presets
        </Typography>
        <Colors>
          {PRESET_COLORS.map((presetColor) => (
            <Color
              key={presetColor}
              color={presetColor}
              onClick={handleColorClick(presetColor)}
            />
          ))}
        </Colors>
        {hasRecentColors && (
          <>
            <Typography variant="small" gutterBottom>
              Recent
            </Typography>
            <Colors>
              {recentColors.map((recentColor) => (
                <Color
                  key={recentColor}
                  color={recentColor}
                  onClick={handleColorClick(recentColor)}
                />
              ))}
            </Colors>
          </>
        )}
      </Stack>
      <Stack
        spacing={1}
        direction="row"
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={handleCancel} variant="text" color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSelect} variant="outlined" color="primary">
          Select
        </Button>
      </Stack>
    </Popover>
  );
};
